import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Gem } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getCustomerToken, setCustomerToken, refreshWishlist } from "@/lib/account";
import { apiError } from "@/lib/admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "gt_promo_seen";
const SHOW_AFTER_MS = 5000;

const inputCls =
  "w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] transition-colors duration-300";

export function PromoPopup() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(null);
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (pathname.startsWith("/admin") || pathname === "/gt-console-9k2x7") return undefined;
    // Signed review/track pages are a post-purchase task — never cover them with a popup.
    if (pathname.startsWith("/review") || pathname.startsWith("/track")) return undefined;
    // A shopper arriving from a search query is mid-task — don't cover the
    // results grid with a popup (owner-approved 2026-10-27).
    if (pathname === "/catalog" && new URLSearchParams(search).get("q")) return undefined;
    if (getCustomerToken() || localStorage.getItem(SEEN_KEY)) return undefined;
    const t = setTimeout(() => {
      // Re-check at fire time — the visitor may have signed in since this timer was set
      if (!getCustomerToken() && !localStorage.getItem(SEEN_KEY)) setOpen(true);
    }, SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, [pathname]);

  // Never show to a signed-in customer, even if a timer fired before login completed —
  // but do keep showing the success code right after they register through the popup
  if (!open || (getCustomerToken() && !code)) return null;

  const close = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/account/register`, { name, email, password, marketing_opt_in: marketingOptIn });
      setCustomerToken(data.token);
      await refreshWishlist();
      setCode(data.welcome_code);
      localStorage.setItem(SEEN_KEY, "1");
      toast.success("Welcome to Gold Time");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-start sm:items-center justify-center p-4 pt-24 sm:pt-4 bg-black/60 backdrop-blur-sm" data-testid="promo-popup" onClick={close}>
      <div
        className="relative w-full max-w-md max-h-[80vh] overflow-y-auto bg-[#FDFBF6] border border-[#C9A227]/40 rounded-sm shadow-[0_30px_80px_rgb(0,0,0,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0D0D0D] px-8 py-5 flex items-center justify-center border-b border-[#C9A227]/30">
          <img src="/assets/logo.png" alt="Gold Time — Fine Jewelry and Watches" className="h-9 w-auto" />
        </div>
        <button onClick={close} aria-label="Close" data-testid="promo-popup-close" className="absolute top-1 right-1 w-11 h-11 flex items-center justify-center text-neutral-500 hover:text-white transition-colors">
          <X size={20} />
        </button>
        <div className="p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px flex-1 bg-[#C9A227]/40" />
            <Gem size={14} className="text-[#C9A227]" />
            <span className="h-px flex-1 bg-[#C9A227]/40" />
          </div>
          {!code ? (
            <>
              <h2 className="font-serif text-2xl lg:text-[28px] leading-snug text-neutral-900 text-center mb-2" data-testid="promo-popup-title">
                Take <span className="text-[#A8871B]">10% Off</span> Your First Piece
              </h2>
              <p className="text-sm text-neutral-600 text-center leading-relaxed mb-6">
                Join the Gold Time family — save pieces you love to your wishlist, track your orders, and we'll send your welcome code right away.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <input required placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="promo-popup-name" />
                <input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="promo-popup-email" />
                <input required type="password" minLength={8} placeholder="Password (8+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="promo-popup-password" />
                <label className="flex items-start gap-2.5 text-left cursor-pointer" data-testid="popup-marketing-consent">
                  <input
                    type="checkbox"
                    checked={marketingOptIn}
                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#C9A227]"
                    data-testid="popup-marketing-checkbox"
                  />
                  <span className="text-xs text-neutral-500 leading-relaxed">
                    Yes — email me occasional offers and new arrivals. Order emails are sent regardless; change anytime in your account.
                  </span>
                </label>
                <button
                  type="submit" disabled={busy}
                  className="w-full rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_12px_30px_rgb(201,162,39,0.25)] disabled:opacity-60"
                  data-testid="promo-popup-submit"
                >
                  {busy ? "Creating your account…" : "Get My 10% Code"}
                </button>
              </form>
              <button onClick={close} className="mt-4 w-full rounded-full border border-[#E8DCC4] py-3 text-center text-sm text-neutral-500 hover:text-neutral-800 hover:border-[#C9A227] transition-colors" data-testid="promo-popup-dismiss">
                No thanks, I'll browse first
              </button>
            </>
          ) : (
            <div className="text-center" data-testid="promo-popup-success">
              <h2 className="font-serif text-2xl lg:text-[28px] text-neutral-900 mb-2">You're Golden</h2>
              <p className="text-sm text-neutral-600 leading-relaxed mb-6">Welcome to the family. Your 10% welcome code is below — we've also emailed it to you.</p>
              <button
                onClick={() => { navigator.clipboard?.writeText(code); toast.success("Code copied"); }}
                className="font-mono text-xl tracking-[0.2em] text-[#A8871B] font-semibold border border-dashed border-[#C9A227] bg-[#C9A227]/5 rounded-sm px-8 py-4 hover:bg-[#C9A227]/15 transition-colors"
                data-testid="promo-popup-code"
                title="Click to copy"
              >
                {code}
              </button>
              <p className="text-xs text-neutral-500 mt-4">Enter it in the promo code box at checkout. One use.</p>
              <button
                onClick={close}
                className="mt-6 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-10 py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_12px_30px_rgb(201,162,39,0.25)]"
                data-testid="promo-popup-shop"
              >
                Start Shopping
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
